import { useEffect, useState } from "react";
import { PencilIcon, TrashIcon, PlusIcon } from "@heroicons/react/outline";
import {
  createEvent,
  getEvents,
  deleteEvent,
  updateEvent,
  duplicateEvent,
} from "../../api/eventsApi";
import PageLoader from "../../components/pageLoader";
import Toast from "../../components/toast";
import ToastContainer from "../../components/toastContainer";
import DeleteConfirmationModal from "../../components/deleteModal";
import { useNavigate } from "react-router-dom";
import EventFormModal from "./EventFormModal";
import { useAuthStore } from "../../store/auth.store";
import { CURRENCY_MAP } from "../../constants";

// ── Choice Modal ──
const EventChoiceModal = ({
  onClose,
  onCreateNew,
  onDuplicate,
  events,
  loading,
}) => {
  const [selectedId, setSelectedId] = useState("");
  const [duplicating, setDuplicating] = useState(false);
  const [error, setError] = useState("");

  const handleDuplicate = async () => {
    if (!selectedId) {
      setError("Please select an event to duplicate.");
      return;
    }
    setDuplicating(true);
    await onDuplicate(selectedId);
    setDuplicating(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-md bg-slate-900 border border-white/10 rounded-xl shadow-xl">
        <div className="px-6 py-4 border-b border-white/10">
          <h2 className="text-white font-semibold text-base">New Event</h2>
          <p className="text-slate-400 text-xs mt-0.5">
            Create a blank event or duplicate all data from an existing one.
          </p>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Create New */}
          <button
            onClick={onCreateNew}
            className="w-full flex items-center gap-4 p-4 rounded-xl border border-white/10 hover:border-pink-500/40 hover:bg-pink-500/5 transition text-left group"
          >
            <div className="w-10 h-10 rounded-full bg-slate-800 group-hover:bg-pink-500/20 flex items-center justify-center shrink-0 transition">
              <svg
                className="w-5 h-5 text-slate-400 group-hover:text-pink-400 transition"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </div>
            <div>
              <p className="text-white text-sm font-medium">Create New Event</p>
              <p className="text-slate-500 text-xs mt-0.5">
                Start fresh with a blank event form.
              </p>
            </div>
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 border-t border-white/10" />
            <span className="text-xs text-slate-500">or</span>
            <div className="flex-1 border-t border-white/10" />
          </div>

          {/* Duplicate */}
          <div className="p-4 rounded-xl border border-white/10 space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center shrink-0">
                <svg
                  className="w-5 h-5 text-slate-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-white text-sm font-medium">
                  Duplicate Existing Event
                </p>
                <p className="text-slate-500 text-xs mt-0.5">
                  Copies all main info, ticket types, pixels, vouchers,
                  affiliates & all settings.
                </p>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center gap-2 text-slate-400 text-sm py-1">
                <div className="w-4 h-4 border-2 border-white/10 border-t-pink-500 rounded-full animate-spin" />
                Loading events...
              </div>
            ) : (
              <select
                value={selectedId}
                onChange={(e) => {
                  setSelectedId(e.target.value);
                  setError("");
                }}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 pr-10 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40 appearance-none bg-no-repeat bg-right"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")`,
                  backgroundSize: "1.25rem",
                  backgroundPosition: "right 0.75rem center",
                }}
              >
                <option value="">Select an event to duplicate...</option>
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.name} — {ev.date_start?.split("T")[0]}
                  </option>
                ))}
              </select>
            )}

            {error && <p className="text-red-400 text-xs">{error}</p>}

            <button
              onClick={handleDuplicate}
              disabled={duplicating || !selectedId}
              className={`w-full py-2 rounded-lg text-sm font-medium transition ${
                duplicating || !selectedId
                  ? "bg-pink-400/50 cursor-not-allowed text-white/60"
                  : "bg-pink-600 hover:bg-pink-700 text-white"
              }`}
            >
              {duplicating ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  Duplicating...
                </span>
              ) : (
                "Duplicate & Create"
              )}
            </button>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-white/10 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800 transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const EventPage = () => {
  const [showChoiceModal, setShowChoiceModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [existingEvents, setExistingEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);

  const isViewOnly = useAuthStore((s) => s.isViewOnly()); // ← CHANGE 2: read flag

  const LIMIT = 10;

  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [deleteEventId, setDeleteEventId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const totalPages = Math.ceil(total / LIMIT);

  const [editingEvent, setEditingEvent] = useState(null);
  const [saving, setSaving] = useState(false);

  const [toast, setToast] = useState(null);

  const navigate = useNavigate();

  const handleNewEventClick = async () => {
    setShowChoiceModal(true);
    try {
      setLoadingEvents(true);
      const data = await getEvents(100, 0);
      setExistingEvents(data.results || []);
    } catch (err) {
      // silent
    } finally {
      setLoadingEvents(false);
    }
  };

  const handleDuplicate = async (eventId) => {
    try {
      await duplicateEvent(eventId);
      setShowChoiceModal(false);
      setToast({ type: "success", message: "Event duplicated successfully." });
      const offset = (page - 1) * LIMIT;
      const data = await getEvents(LIMIT, offset);
      setEvents(data.results || []);
      setTotal(data.count || 0);
    } catch (err) {
      setToast({
        type: "error",
        message: err?.response?.data?.detail || "Failed to duplicate event.",
      });
    }
  };

  const getPageNumbers = () => {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  };

  const formatCurrency = (amount, currency) => {
    const symbol = CURRENCY_MAP[currency];
    return `${symbol} ${parseFloat(amount).toFixed(2)}`;
  };

  const statusStyle = (status) => {
    switch (status) {
      case "published":
        return "text-emerald-400 bg-emerald-500/10 border-emerald-500/20";
      case "draft":
        return "text-amber-400 bg-amber-500/10 border-amber-500/20";
      case "cancelled":
        return "text-red-400 bg-red-500/10 border-red-500/20";
      default:
        return "text-slate-400 bg-slate-500/10 border-slate-500/20";
    }
  };

  const statusLabel = (status) => {
    switch (status) {
      case "published":
        return "Published";
      case "cancelled":
        return "Cancelled";
      case "draft":
        return "Draft";
      case "completed":
        return "Completed";
      default:
        return status;
    }
  };

  const handleEventSubmit = async (info) => {
    try {
      setSaving(true);

      const formData = new FormData();

      formData.append("name", info.name);
      formData.append("date_start", info.date_start);
      formData.append("date_end", info.date_end);
      // formData.append("sold_tickets", info.sold_tickets);
      // formData.append("invitations_sent", info.invitations_sent);
      // formData.append("refundable_tickets", info.refundable_tickets);
      // formData.append("amount", info.amount);
      //. formData.append("currency", info.currency);

      // Only append poster if it's a File
      // if (info.poster instanceof File) {
      //   formData.append("poster", info.poster);
      // }

      formData.append("map_address", info.map_address);
      formData.append("map_latitude", info.map_latitude);
      formData.append("map_longitude", info.map_longitude);

      // Duplicate event — copy tickets, pixels, vouchers, affiliates etc.
      if (info.duplicate_event) {
        formData.append("duplicate_event", info.duplicate_event);
      }

      if (info.flyer_portrait instanceof File) {
        formData.append("flyer_portrait", info.flyer_portrait);
      }

      if (info.flyer_horizontal instanceof File) {
        formData.append("flyer_horizontal", info.flyer_horizontal);
      }

      if (editingEvent) {
        await updateEvent(editingEvent.id, formData);
      } else {
        await createEvent(formData);
      }

      setEditingEvent(null);
      setShowAddModal(false);

      const offset = (page - 1) * LIMIT;
      const data = await getEvents(LIMIT, offset);
      setEvents(data.results || []);
      setTotal(data.count || 0);
    } catch (err) {
      setToast({
        type: "error",
        message: err?.response?.data?.detail || "Something went wrong",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEventDelete = async () => {
    try {
      setDeleting(true);
      await deleteEvent(deleteEventId);

      setDeleteEventId(null);

      // Refresh events list
      const offset = (page - 1) * LIMIT;
      const data = await getEvents(LIMIT, offset);
      setEvents(data.results || []);
      setTotal(data.count || 0);
    } catch (err) {
      alert("Failed to delete the event");
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        setError("");

        const offset = (page - 1) * LIMIT;
        const data = await getEvents(LIMIT, offset);
        console.log("--", data);

        setEvents(data.results || []);
        setTotal(data.count || 0);
      } catch (err) {
        setError("Failed to load events.");
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [page]);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Events Info</h1>
        </div>

        {/* hide New Event button for viewers */}
        {!isViewOnly && (
          <button
            onClick={handleNewEventClick}
            className="flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
          >
            <PlusIcon className="w-4 h-4" />
            New Event
          </button>
        )}
      </div>

      {error && <div className="p-6 text-red-400">{error}</div>}

      {/* Table */}
      <div className="bg-slate-900/60 border border-white/10 rounded-xl">
        <div className="overflow-x-auto">
          <table className="min-w-[900px] w-full border-collapse">
            <thead>
              <tr className="border-b border-white/10 text-left text-xs uppercase tracking-wider text-slate-400">
                <th className="px-6 py-4 text-center">Poster</th>
                <th className="px-6 py-4 text-center">Name</th>
                <th className="px-6 py-4 text-center">Event start date</th>
                <th className="px-6 py-4 text-center">Event end date</th>
                <th className="px-6 py-4 text-center">Refunded tickets</th>
                <th className="px-6 py-4 text-center">Sold tickets</th>
                <th className="px-6 py-4 text-center">Invitations send</th>
                <th className="px-6 py-4 text-center">To be settled</th>
                <th className="px-6 py-4 text-center">Status</th>
                {/* hide Actions column header for viewers */}
                {!isViewOnly && (
                  <th className="px-6 py-4 text-center">Actions</th>
                )}
              </tr>
            </thead>

            <tbody>
              {events.map((event) => (
                <tr
                  key={event.id}
                  onClick={() => navigate(`/dashboard/events/${event.id}`)}
                  className="border-b border-white/5 last:border-none hover:bg-slate-800/40 transition cursor-pointer"
                >
                  <td className="px-6 py-4">
                    {event.flyer_portrait ? (
                      <img
                        className="w-20 h-20 object-contain rounded-sm"
                        src={event.flyer_portrait}
                        alt="-"
                      />
                    ) : (
                      <div className="text-sm text-center font-medium text-slate-300">
                        -
                      </div>
                    )}
                  </td>

                  <td className="px-6 py-4">
                    <div className="text-sm text-center font-medium text-slate-300">
                      {event.name}
                    </div>
                  </td>

                  <td className="px-6 py-4 text-center text-sm text-slate-300">
                    {event.date_start.split("T")[0]}
                  </td>

                  <td className="px-6 py-4 text-center text-sm text-slate-300">
                    {event.date_end.split("T")[0]}
                  </td>

                  <td className="px-6 py-4 text-center text-sm text-slate-300">
                    {event.refunded_tickets_count
                      ? `${event.refunded_tickets_count} tickets`
                      : "-"}
                      <br/>
                      {event.refunded_tickets_count ? `(${formatCurrency(event.refunded_tickets_amount,event.currency)})` : ''}
                  </td>

                  <td className="px-6 py-4 text-center text-sm text-slate-300">
                    {event.sold_tickets}
                  </td>

                  <td className="px-6 py-4 text-center text-sm text-slate-300">
                    {event.invitations_sent}
                  </td>

                  <td className="px-6 py-4 text-center text-sm text-slate-300">
                    {event.currency} {event.amount}
                  </td>

                  <td className="px-6 py-4 text-center">
                    <span
                      className={`px-2.5 py-0.5 rounded-md text-xs font-medium border ${statusStyle(event.status)}`}
                    >
                      {statusLabel(event.status)}
                    </span>
                  </td>

                  {/* hide edit/delete buttons for viewers */}
                  {!isViewOnly && (
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingEvent(event);
                          }}
                          className="p-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-700 transition"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteEventId(event.id);
                          }}
                          className="p-2 rounded-md text-slate-300 hover:text-red-400 hover:bg-red-500/10 transition"
                          title="Delete event"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {events.length === 0 && (
          <div className="p-8 text-center text-slate-400">No events found.</div>
        )}
      </div>

      <div className="flex justify-end">
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 text-sm text-slate-400">
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 px-6 py-4 text-sm text-slate-400">
                {/* Previous */}
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className={`px-3 py-1 rounded-md border border-white/10 transition
        ${
          page === 1
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-slate-800 text-white"
        }`}
                >
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="flex gap-1">
                  {getPageNumbers().map((pageNumber) => (
                    <button
                      key={pageNumber}
                      onClick={() => setPage(pageNumber)}
                      className={`px-3 py-1 rounded-md border border-white/10 transition
            ${
              page === pageNumber
                ? "bg-pink-600 text-white"
                : "text-slate-300 hover:bg-slate-800 hover:text-white"
            }`}
                    >
                      {pageNumber}
                    </button>
                  ))}
                </div>

                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className={`px-3 py-1 rounded-md border border-white/10 transition
        ${
          page === totalPages
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-slate-800 text-white"
        }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals only mount for non-viewers — no way to trigger them anyway */}
      {!isViewOnly && (
        <>
          {/* Choice modal — shown when clicking New Event */}
          {showChoiceModal && (
            <EventChoiceModal
              onClose={() => setShowChoiceModal(false)}
              onCreateNew={() => {
                setShowChoiceModal(false);
                setShowAddModal(true);
              }}
              onDuplicate={handleDuplicate}
              events={existingEvents}
              loading={loadingEvents}
            />
          )}

          <EventFormModal
            isOpen={showAddModal || !!editingEvent}
            onClose={() => {
              setShowAddModal(false);
              setEditingEvent(null);
            }}
            onSubmit={handleEventSubmit}
            loading={saving}
            mode={editingEvent ? "edit" : "create"}
            initialData={editingEvent}
          />

          <DeleteConfirmationModal
            isOpen={!!deleteEventId}
            description="Are you sure you want to delete this event? This action cannot be undone."
            onClose={() => setDeleteEventId(null)}
            onConfirm={handleEventDelete}
            loading={deleting}
          />
        </>
      )}

      <ToastContainer>
        {toast && (
          <div className="toast-enter">
            <Toast
              type={toast.type}
              message={toast.message}
              onClose={() => setToast(null)}
            />
          </div>
        )}
      </ToastContainer>
    </div>
  );
};

export default EventPage;
